export const COMPARISON_RISK_COMMENT_TYPE = "comparison_risk";
export const COMPARISON_CHANGE_COMMENT_TYPE = "comparison_change";
export const COMPARISON_GENERIC_COMMENT_TYPES = new Set(["comparison", "comparaison"]);
export const REFERENCE_COMPARISON_CRITERION_TYPE = "document_comparison";
export const REFERENCE_REVIEW_CRITERION_TYPE = "reference_review";
export const DEFAULT_COMPARISON_CHANGE_CONFIDENCE_THRESHOLD = 70;

const REFERENCE_COMPARISON_TITLE =
    "risques introduits par rapport a une version de reference";

export const normalizeComparisonText = (value) => String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

export const getComparisonMetadata = (comment) => {
    const raw = comment?.metadata_json ?? comment?.metadataJson ?? null;
    let parsed = null;
    if (raw && typeof raw === "string") {
        try {
            parsed = JSON.parse(raw);
        } catch (_) {
            parsed = null;
        }
    } else if (raw && typeof raw === "object") {
        parsed = raw;
    }

    if (parsed && typeof parsed === "object") return parsed;

    const fallback = {};
    const metadataKeys = [
        "analysis_scope",
        "base_block_ids",
        "base_section",
        "base_text",
        "candidate_index",
        "change_summary",
        "change_type",
        "comparison_candidate_index",
        "comparison_document_id",
        "confidence_count",
        "coverage_summary",
        "is_exhaustive",
        "match_confidence",
        "match_confidence_score",
        "match_method",
        "match_score",
        "matching_method",
        "matching_score",
        "new_block_ids",
        "new_section",
        "new_text",
        "omitted_blocks",
        "omitted_reason",
        "requires_llm_judgment",
        "risk_fallback",
        "similarity",
        "similarity_score",
        "truncated",
        "unresolved_block_count",
    ];
    for (const key of metadataKeys) {
        if (comment?.[key] !== undefined) fallback[key] = comment[key];
    }
    return Object.keys(fallback).length ? fallback : null;
};

export const normalizeComparisonCommentType = (comment) => {
    const commentType = normalizeComparisonText(comment?.comment_type ?? comment?.commentType);
    if (commentType === COMPARISON_CHANGE_COMMENT_TYPE) return COMPARISON_CHANGE_COMMENT_TYPE;
    if (
        commentType === COMPARISON_RISK_COMMENT_TYPE
        || COMPARISON_GENERIC_COMMENT_TYPES.has(commentType)
        || normalizeComparisonText(getComparisonMetadata(comment)?.analysis_scope) === "comparison"
    ) {
        return COMPARISON_RISK_COMMENT_TYPE;
    }
    return comment?.comment_type ?? comment?.commentType ?? null;
};

const COMPARISON_CHANGE_SUMMARY_KEYS = {
    "possible match found, but similarity is too weak for reliable classification.":
        "comparison.summary.weak_match",
    "clause present in the received contract with no reliable reference equivalent.":
        "comparison.summary.added_without_reference",
    "reference clause has no reliable equivalent in the received contract.":
        "comparison.summary.removed_without_received",
    "clause appears materially similar but moved to a different position.":
        "comparison.summary.reordered_clause",
    "reference and received clauses are similar but materially changed.":
        "comparison.summary.modified_clause",
};

const COMPARISON_CHANGE_SUMMARY_DEFAULT_FR = {
    "possible match found, but similarity is too weak for reliable classification.":
        "Correspondance probable entre les deux versions, mais insuffisante pour une classification certaine.",
    "clause present in the received contract with no reliable reference equivalent.":
        "Clause présente dans le contrat reçu sans équivalent fiable dans la référence.",
    "reference clause has no reliable equivalent in the received contract.":
        "Clause présente dans la référence sans équivalent fiable dans le contrat reçu.",
    "clause appears materially similar but moved to a different position.":
        "Clause substantiellement similaire, mais déplacée dans le contrat reçu.",
    "reference and received clauses are similar but materially changed.":
        "Clause similaire à la référence, mais modifiée de manière significative.",
};

const COMPARISON_VALUE_FR = {
    added_clause: "Clause ajoutée",
    removed_clause: "Clause retirée",
    modified_clause: "Clause modifiée",
    reordered_clause: "Clause déplacée",
    unchanged_or_minor_change: "Changement mineur",
    low_confidence_match: "Rapprochement à confirmer",
    high: "élevée",
    medium: "moyenne",
    low: "faible",
    hybrid: "hybride",
    heuristic: "heuristique",
    lexical: "lexicale",
    fallback: "secours",
    pgvector: "vectorielle",
};

export const formatComparisonMetadataValue = (value, t = null) => {
    if (value === null || value === undefined || value === "") return null;
    const raw = String(value).trim();
    const normalized = normalizeComparisonText(raw).replace(/\s+/g, "_");
    if (t) {
        const translated = t(`comparison.values.${normalized}`, { defaultValue: "" });
        if (translated) return translated;
    }
    return COMPARISON_VALUE_FR[normalized] || raw.replace(/_/g, " ");
};

export const formatComparisonChangeSummary = (value, changeType = null, t = null) => {
    const text = String(value ?? "")
        .replace(/\s+/g, " ")
        .trim();
    const normalized = normalizeComparisonText(text);
    if (COMPARISON_CHANGE_SUMMARY_KEYS[normalized]) {
        const key = COMPARISON_CHANGE_SUMMARY_KEYS[normalized];
        return t
            ? t(key, { defaultValue: COMPARISON_CHANGE_SUMMARY_DEFAULT_FR[normalized] })
            : COMPARISON_CHANGE_SUMMARY_DEFAULT_FR[normalized];
    }
    if (!text) {
        return formatComparisonMetadataValue(changeType, t) || "";
    }
    return text;
};

export const isComparisonRiskComment = (comment) => {
    const commentType = normalizeComparisonText(comment?.comment_type ?? comment?.commentType);
    const metadata = getComparisonMetadata(comment);
    return (
        commentType === COMPARISON_RISK_COMMENT_TYPE
        || commentType === COMPARISON_CHANGE_COMMENT_TYPE
        || COMPARISON_GENERIC_COMMENT_TYPES.has(commentType)
        || normalizeComparisonText(metadata?.analysis_scope) === "comparison"
    );
};

export const isComparisonChangeComment = (comment) => {
    const commentType = normalizeComparisonText(comment?.comment_type ?? comment?.commentType);
    return commentType === COMPARISON_CHANGE_COMMENT_TYPE;
};

export const isComparisonRiskFallback = (comment) => {
    const metadata = getComparisonMetadata(comment);
    return Boolean(metadata?.risk_fallback === true);
};

export const getComparisonConfidenceScore = (commentOrMetadata) => {
    const metadata = getComparisonMetadata(commentOrMetadata) || commentOrMetadata || {};
    const raw = metadata?.confidence_score
        ?? metadata?.match_confidence_score
        ?? metadata?.match_score
        ?? metadata?.similarity
        ?? metadata?.matching_score
        ?? metadata?.similarity_score
        ?? metadata?.match_confidence
        ?? null;

    if (raw === null || raw === undefined || raw === "") return null;

    if (typeof raw === "number") {
        const score = raw <= 1 ? raw * 100 : raw;
        return Math.max(0, Math.min(100, Math.round(score)));
    }

    const text = String(raw).trim();
    const numericMatch = text.match(/(\d+(?:[.,]\d+)?)/);
    if (numericMatch) {
        const parsed = Number(numericMatch[1].replace(",", "."));
        if (Number.isFinite(parsed)) {
            const score = parsed <= 1 ? parsed * 100 : parsed;
            return Math.max(0, Math.min(100, Math.round(score)));
        }
    }

    const normalized = normalizeComparisonText(text);
    if (normalized === "high" || normalized === "elevee") return 90;
    if (normalized === "medium" || normalized === "moyenne" || normalized === "moyen") return 70;
    if (normalized === "low" || normalized === "faible") return 45;
    return null;
};

export const getComparisonConfidenceBand = (commentOrMetadata) => {
    const score = getComparisonConfidenceScore(commentOrMetadata);
    if (score === null) return "unknown";
    if (score >= 80) return "high";
    if (score >= DEFAULT_COMPARISON_CHANGE_CONFIDENCE_THRESHOLD) return "medium";
    return "low";
};

export const hasComparisonCoverageWarning = (comment) => {
    const metadata = getComparisonMetadata(comment);
    return Boolean(metadata?.truncated === true || metadata?.is_exhaustive === false);
};

export const getComparisonCoverageInfo = (commentOrMetadata, t = null) => {
    const metadata = getComparisonMetadata(commentOrMetadata) || commentOrMetadata || {};
    if (!metadata || typeof metadata !== "object") return null;

    const summary = String(metadata.coverage_summary || "").trim();
    const sectionsMatch = summary.match(/covered\s+(\d+)\s+reference\s+blocks?\s+and\s+(\d+)\s+received\s+blocks?/i);
    const sectionsLabel = sectionsMatch
        ? `${Math.max(Number(sectionsMatch[1]), Number(sectionsMatch[2]))} sections comparees`
        : "";

    if (metadata.truncated === true) {
        const label = t
            ? t("comparison.coverage_truncated", "Comparaison tronquee")
            : "Comparaison tronquee";
        return {
            status: "truncated",
            label,
            shortLabel: sectionsLabel ? `${label} · ${sectionsLabel}` : label,
            tooltip: summary || label,
        };
    }

    if (metadata.is_exhaustive === false) {
        const label = t
            ? t("comparison.coverage_partial", "Comparaison partielle")
            : "Comparaison partielle";
        return {
            status: "partial",
            label,
            shortLabel: sectionsLabel ? `${label} · ${sectionsLabel}` : label,
            tooltip: summary || label,
        };
    }

    return null;
};

export const hasComparisonAmbiguity = (comment) => {
    const metadata = getComparisonMetadata(comment);
    return Boolean(
        metadata?.requires_llm_judgment === true
        || normalizeComparisonText(metadata?.match_confidence) === "low"
        || normalizeComparisonText(metadata?.change_type) === "low_confidence_match"
    );
};

export const isDisplayableComparisonChange = (
    comment,
    threshold = DEFAULT_COMPARISON_CHANGE_CONFIDENCE_THRESHOLD
) => {
    if (!isComparisonChangeComment(comment) && !isComparisonRiskFallback(comment)) return false;
    if (isComparisonRiskFallback(comment)) return true;
    const score = getComparisonConfidenceScore(comment);
    if (score === null) return false;
    if (hasComparisonAmbiguity(comment)) return false;
    return score >= threshold;
};

export const isPrimaryRiskComment = (comment) => (
    !isComparisonChangeComment(comment)
    && !isComparisonRiskFallback(comment)
);

const GENERIC_COMPARISON_CHANGE_TEXTS = new Set([
    "changement",
    "changement detecte",
    "risque de comparaison a qualifier",
    "a qualifier",
    "a negocier ou valider selon l'allocation de risque.",
    "a clarifier avant signature : qualifier l'allocation de risque.",
    "a clarifier avant signature : qualifier le changement.",
    "clause similaire a la reference, mais modifiee de maniere significative.",
    "qualifier l'impact du changement et decider s'il doit etre accepte, negocie ou documente.",
    "qualifier l'impact du changement et decider s'il doit etre negocie, documente ou valide.",
    "difference non prioritaire a qualifier avant negociation.",
]);

const isGenericComparisonChangeText = (value) => {
    const normalized = normalizeComparisonText(value);
    if (!normalized) return true;
    if (GENERIC_COMPARISON_CHANGE_TEXTS.has(normalized)) return true;
    return (
        normalized.includes("clause similaire")
        && normalized.includes("modifi")
    );
};

const inferComparisonChangeDescription = (comment) => {
    const metadata = getComparisonMetadata(comment) || {};
    const haystack = normalizeComparisonText([
        metadata.new_section,
        metadata.base_section,
        metadata.new_text,
        metadata.base_text,
        comment?.title,
        comment?.quote,
    ].filter(Boolean).join(" "));

    if (haystack.includes("suspend") && haystack.includes("paiement")) {
        return "Ajout d'un droit de suspension des paiements au benefice du Client.";
    }
    if (haystack.includes("responsabilite") || haystack.includes("dommages indirects") || haystack.includes("plafond")) {
        return "Modification du plafond de responsabilite ou des exclusions de dommages.";
    }
    if (haystack.includes("garantie") && haystack.includes("ia")) {
        return "Modification des garanties ou limitations relatives a l'IA.";
    }
    if (haystack.includes("sla") || haystack.includes("disponibilite") || haystack.includes("credit")) {
        return "Modification des engagements SLA ou des credits de service.";
    }
    if (haystack.includes("indemnisation")) {
        return "Modification du regime d'indemnisation entre les parties.";
    }
    if (haystack.includes("resiliation")) {
        return "Modification des droits ou delais de resiliation.";
    }
    if (haystack.includes("donnees personnelles") || haystack.includes("confidentialite") || haystack.includes("localisation")) {
        return "Modification des obligations relatives aux donnees personnelles ou a la confidentialite.";
    }
    if (haystack.includes("juridiction") || haystack.includes("tribunaux") || haystack.includes("differend")) {
        return "Modification de la juridiction competente ou du reglement des differends.";
    }
    if (haystack.includes("audit")) {
        return "Modification des droits d'audit ou de controle contractuel.";
    }

    return "Clause modifiee avec impact contractuel significatif.";
};

const asSecondaryComparisonQuickView = (comment, forceDescription = false) => {
    const quickView = comment?.quick_view || {};
    const nextQuickView = {
        ...(quickView && typeof quickView === "object" ? quickView : {}),
    };
    if (isGenericComparisonChangeText(nextQuickView.risk)) {
        nextQuickView.risk = "Changement detecte";
    }
    if (isGenericComparisonChangeText(nextQuickView.action)) {
        nextQuickView.action = "À clarifier avant signature : qualifier le changement.";
    }
    if (forceDescription || isGenericComparisonChangeText(nextQuickView.description)) {
        nextQuickView.description = inferComparisonChangeDescription(comment);
    }
    return nextQuickView;
};

const asUncertainComparisonChange = (comment, language = "fr") => {
    const english = String(language || "").toLowerCase().startsWith("en");
    const metadata = getComparisonMetadata(comment) || {};
    const baseSection = String(metadata.base_section || "").trim();
    const newSection = String(metadata.new_section || "").trim();
    const distinctSections = baseSection
        && newSection
        && normalizeComparisonText(baseSection) !== normalizeComparisonText(newSection);
    const sectionLabel = distinctSections
        ? `${baseSection} ↔ ${newSection}`
        : (newSection || baseSection);
    const title = sectionLabel
        ? `${english ? "Match to confirm" : "Rapprochement à confirmer"} — ${sectionLabel}`
        : (english ? "Match to confirm" : "Rapprochement à confirmer");
    const description = sectionLabel
        ? (english
            ? `Verdictia could not reliably confirm the proposed match (${sectionLabel}). Its impact on the studied party cannot yet be assessed.`
            : `Verdictia n’a pas pu confirmer de façon fiable le rapprochement proposé (${sectionLabel}). Son impact pour la partie étudiée ne peut pas encore être évalué.`)
        : (english
            ? "Verdictia could not confirm that both passages address the same provision. Their impact on the studied party cannot yet be assessed."
            : "Verdictia n’a pas pu confirmer que ces deux passages portent sur la même disposition. Leur impact pour la partie étudiée ne peut pas encore être évalué.");
    const action = english
        ? "Confirm before signing that these passages should actually be compared."
        : "À vérifier avant signature : confirmer que ces passages doivent réellement être comparés.";

    return {
        ...comment,
        title,
        description,
        quick_view: {
            risk: english ? "Unconfirmed match" : "Rapprochement non confirmé",
            action,
            description,
        },
        suggested_action: action,
    };
};

export const asSecondaryComparisonChange = (comment, language = "fr") => {
    const metadata = getComparisonMetadata(comment) || {};
    if (hasComparisonAmbiguity(comment)) {
        return asUncertainComparisonChange(comment, language);
    }
    const section = metadata.new_section || metadata.base_section;
    if (!isComparisonRiskFallback(comment)) {
        return {
            ...comment,
            quick_view: asSecondaryComparisonQuickView(comment),
        };
    }
    return {
        ...comment,
        title: section ? `Changement detecte - ${section}` : "Changement detecte",
        comment_type: COMPARISON_CHANGE_COMMENT_TYPE,
        risk: null,
        quick_view: asSecondaryComparisonQuickView(comment, true),
        suggested_action: "À clarifier avant signature : qualifier le changement.",
    };
};

export const splitPrimaryAndSecondaryComparisonComments = (comments = [], language = "fr") => {
    const primary = [];
    const comparisonChanges = [];
    const uncertainComparisonChanges = [];
    for (const comment of comments || []) {
        if (isComparisonChangeComment(comment) || isComparisonRiskFallback(comment)) {
            const secondaryComment = asSecondaryComparisonChange(comment, language);
            if (isDisplayableComparisonChange(secondaryComment)) {
                comparisonChanges.push(secondaryComment);
            } else {
                uncertainComparisonChanges.push(secondaryComment);
            }
        } else {
            primary.push(comment);
        }
    }
    return { primary, comparisonChanges, uncertainComparisonChanges };
};

export const isReferenceComparisonCriterion = (criterion) => {
    if (!criterion) return false;
    const criterionType = normalizeComparisonText(
        criterion.criterion_type ?? criterion.criterionType
    );
    return criterionType === REFERENCE_COMPARISON_CRITERION_TYPE;
};

export const isReferenceReviewCriterion = (criterion) => {
    if (!criterion) return false;
    return normalizeComparisonText(
        criterion.criterion_type ?? criterion.criterionType
    ) === REFERENCE_REVIEW_CRITERION_TYPE;
};

export const hasCompletedReferenceReviewCriterion = (criteria = []) => (
    (criteria || []).some((criterion) => (
        isReferenceReviewCriterion(criterion)
        && normalizeComparisonText(criterion?.status) === "completed"
    ))
);

export const getSelectionReferenceIds = (selection) => {
    const rawRefs = selection?.ref
        ?? selection?.reference_file_ids
        ?? selection?.referenceFileIds
        ?? [];
    const refs = Array.isArray(rawRefs) ? rawRefs : [rawRefs];
    return refs.filter(Boolean);
};

const UUID_REFERENCE_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const getValidUniqueReferenceIds = (selection) => Array.from(new Set(
    getSelectionReferenceIds(selection)
        .map((referenceId) => String(referenceId).trim().toLowerCase())
        .filter((referenceId) => UUID_REFERENCE_RE.test(referenceId))
));

export const REFERENCE_SELECTION_ERROR_CODES = Object.freeze({
    DOCUMENT_COMPARISON: "document_comparison_requires_one_reference",
    REFERENCE_REVIEW: "reference_review_requires_one_reference",
});

export const REFERENCE_SELECTION_ERROR_REASONS = Object.freeze({
    MISSING: "missing_reference",
    MULTIPLE: "multiple_references",
});

export const getReferenceSelectionIssue = (selection, criterion) => {
    const isComparison = isReferenceComparisonCriterion(criterion);
    const isReferenceReview = isReferenceReviewCriterion(criterion);
    if (!isComparison && !isReferenceReview) return null;

    const referenceCount = isReferenceReview
        ? getValidUniqueReferenceIds(selection).length
        : getSelectionReferenceIds(selection).length;
    const invalid = isReferenceReview
        ? referenceCount !== 1
        : referenceCount === 0;
    if (!invalid) return null;

    return {
        code: isReferenceReview
            ? REFERENCE_SELECTION_ERROR_CODES.REFERENCE_REVIEW
            : REFERENCE_SELECTION_ERROR_CODES.DOCUMENT_COMPARISON,
        reason: referenceCount === 0
            ? REFERENCE_SELECTION_ERROR_REASONS.MISSING
            : REFERENCE_SELECTION_ERROR_REASONS.MULTIPLE,
        criterion_id: selection?.id || criterion?.id || null,
        reference_count: referenceCount,
        selection,
    };
};

export const findInvalidReferenceSelection = (selectedCriteria = [], criteria = []) => {
    const criteriaById = new Map((criteria || []).map((criterion) => [criterion.id, criterion]));
    for (const selection of selectedCriteria || []) {
        const issue = getReferenceSelectionIssue(selection, criteriaById.get(selection?.id));
        if (issue) return issue;
    }
    return null;
};

export const findMissingReferenceComparisonSelection = (selectedCriteria = [], criteria = []) => {
    return findInvalidReferenceSelection(selectedCriteria, criteria)?.selection || null;
};

export const getComparisonReviewMode = (selectedCriteria = [], criteria = []) => {
    if (!Array.isArray(selectedCriteria) || selectedCriteria.length !== 1) return null;
    const criteriaById = new Map((criteria || []).map((criterion) => [criterion.id, criterion]));
    const criterion = criteriaById.get(selectedCriteria[0]?.id);
    if (isReferenceComparisonCriterion(criterion)) return 'comparison';
    if (isReferenceReviewCriterion(criterion)) return 'reference_review';
    return null;
};

export const isRemovedComparisonClause = (comment) => (
    normalizeComparisonText(getComparisonMetadata(comment)?.change_type) === "removed_clause"
);

export const hasReceivedContractAnchor = (comment) => {
    const metadata = getComparisonMetadata(comment) || {};
    const ref = String(comment?.ref ?? comment?.target_paragraph_id ?? "").trim();
    if (ref && ref !== "global") return true;

    const highlights = Array.isArray(comment?.highlights) ? comment.highlights : [];
    if (highlights.some((highlight) => highlight?.bbox)) return true;
    if (comment?.bbox) return true;
    if (
        comment?.bbox_left !== null
        && comment?.bbox_left !== undefined
        && comment?.bbox_top !== null
        && comment?.bbox_top !== undefined
        && comment?.bbox_width !== null
        && comment?.bbox_width !== undefined
        && comment?.bbox_height !== null
        && comment?.bbox_height !== undefined
    ) {
        return true;
    }

    const targetBlockIds = Array.isArray(comment?.target_block_ids)
        ? comment.target_block_ids
        : Array.isArray(comment?.targetBlockIds)
            ? comment.targetBlockIds
            : [];
    if (targetBlockIds.some(Boolean)) return true;

    const metadataTargetBlockIds = Array.isArray(metadata?.new_block_ids)
        ? metadata.new_block_ids
        : Array.isArray(metadata?.newBlockIds)
            ? metadata.newBlockIds
            : Array.isArray(metadata?.target_block_ids)
                ? metadata.target_block_ids
                : Array.isArray(metadata?.targetBlockIds)
                    ? metadata.targetBlockIds
                    : [];
    return metadataTargetBlockIds.some(Boolean);
};

export const getCommentHighlightRef = (comment) => {
    if (!comment) return null;
    if (!isComparisonRiskComment(comment)) {
        return comment?.ref || comment?.target_paragraph_id || null;
    }

    if (isRemovedComparisonClause(comment) || !hasReceivedContractAnchor(comment)) {
        return null;
    }

    const targetBlockIds = Array.isArray(comment?.target_block_ids)
        ? comment.target_block_ids
        : Array.isArray(comment?.targetBlockIds)
            ? comment.targetBlockIds
            : [];
    const firstTargetBlockId = targetBlockIds.find(Boolean);
    if (firstTargetBlockId) return firstTargetBlockId;

    const metadata = getComparisonMetadata(comment) || {};
    const metadataTargetBlockIds = Array.isArray(metadata?.new_block_ids)
        ? metadata.new_block_ids
        : Array.isArray(metadata?.newBlockIds)
            ? metadata.newBlockIds
            : Array.isArray(metadata?.target_block_ids)
                ? metadata.target_block_ids
                : Array.isArray(metadata?.targetBlockIds)
                    ? metadata.targetBlockIds
                    : [];
    const firstMetadataTargetBlockId = metadataTargetBlockIds.find(Boolean);
    if (firstMetadataTargetBlockId) return firstMetadataTargetBlockId;

    const highlights = Array.isArray(comment?.highlights) ? comment.highlights : [];
    const firstHighlightId = highlights.map((highlight) => highlight?.block_id || highlight?.blockId).find(Boolean);
    if (firstHighlightId) return firstHighlightId;

    return comment?.ref || comment?.target_paragraph_id || null;
};

export const getHighlightableCommentRef = (comment) => {
    return getCommentHighlightRef(comment);
};
